import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FileText, PlusCircle, Download, Trash2, RefreshCw, FolderOpen, ArrowDownWideNarrow, ArrowUpWideNarrow } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient, backendUrl, getToken } from "@/lib/api";
import { CategoryColors } from "@/lib/colors";
import { fmtMoney } from "@/lib/money";
import { isAdmin, visibleCategories, CATEGORY_ROLES, defaultCategory, canSubmitMultiple, userCategories } from "@/lib/roles";
import { PageHeader, StatCard, EmptyState, PageLoader, CategoryBadge } from "@/components/shared/ui";
import FacilityPicker from "@/components/shared/FacilityPicker";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const cats = visibleCategories(user);

  const [stats, setStats] = useState(null);
  const [estimations, setEstimations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory(user));
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [showPicker, setShowPicker] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const deletedParam = admin && showDeleted ? "&include_deleted=true" : "";
      const [statsData, estData] = await Promise.all([
        apiClient.get("/dashboard/stats"),
        apiClient.get(`/estimations?category=${selectedCategory}&limit=10&sort_order=${sortOrder}${deletedParam}`),
      ]);
      setStats(statsData);
      setEstimations(estData.estimations || []);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showDeleted, admin, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRestore = async (id) => {
    try {
      await apiClient.post(`/estimations/${id}/restore`, {});
      fetchData();
    } catch (e) {
      /* ignore */
    }
  };

  const handleExport = (format) => {
    const url = `${backendUrl()}/api/export?format=${format}&category=${selectedCategory}&token=${getToken()}`;
    window.open(url, "_blank");
  };

  const goSubmit = () => {
    if (canSubmitMultiple(user)) setShowPicker(true);
    else navigate(`/estimation/${userCategories(user)[0] || "structure"}`);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t("dashboard")}
        subtitle={admin ? t("super_admin") : `${t(user?.role || "")} ${t("engineer")}`}
        actions={
          <button
            data-testid="submit-new-estimation-btn"
            onClick={goSubmit}
            className="flex items-center gap-2 rounded-xl bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <PlusCircle className="h-4 w-4" /> {t("submit_new_estimation_btn")}
          </button>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard icon={Building2} value={stats?.total_units || 0} label={t("total_units")} color="#2563EB" />
        <StatCard icon={FileText} value={stats?.total_estimations || 0} label={t("total_estimations")} color="#059669" />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cats.map((cat) => (
          <div key={cat} className="rounded-xl border border-slate-200 border-s-4 bg-white p-4" style={{ borderInlineStartColor: CategoryColors[cat] }}>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: CategoryColors[cat] }}>
              {t(cat)}
            </div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{stats?.categories?.[cat]?.total || 0}</div>
          </div>
        ))}
      </div>

      {visibleCategories(user).length > 1 ? (
        <div className="mb-5 flex flex-wrap gap-2">
          {visibleCategories(user).map((cat) => (
            <button
              key={cat}
              data-testid={`category-tab-${cat}`}
              onClick={() => setSelectedCategory(cat)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                selectedCategory === cat ? "border-[var(--brand)] bg-[var(--brand)] text-white" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              {t(cat)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t("recent_estimations")}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="sort-order-toggle"
            onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            {sortOrder === "desc" ? <ArrowDownWideNarrow className="h-3.5 w-3.5" /> : <ArrowUpWideNarrow className="h-3.5 w-3.5" />}
            {sortOrder === "desc" ? t("descending") : t("ascending")}
          </button>
          {admin ? (
            <>
            <button
              data-testid="show-deleted-toggle"
              onClick={() => setShowDeleted((s) => !s)}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                showDeleted ? "border-red-200 bg-red-50 text-red-500" : "border-slate-200 bg-slate-50 text-slate-500"
              }`}
            >
              <Trash2 className="h-3.5 w-3.5" /> {t("show_deleted")}
            </button>
            <button data-testid="export-csv-btn" onClick={() => handleExport("csv")} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[var(--brand)] hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> {t("csv")}
            </button>
            <button data-testid="export-excel-btn" onClick={() => handleExport("excel")} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> {t("excel")}
            </button>
            </>
          ) : null}
        </div>
      </div>

      {estimations.length === 0 ? (
        <EmptyState icon={FolderOpen} title={t("no_estimations")} subtitle={t("submit_via_units")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {estimations.map((est) => (
            <div
              key={est.id}
              data-testid={`estimation-item-${est.id}`}
              onClick={() => navigate(`/estimation/view/${est.id}`)}
              className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded-lg bg-[var(--brand-light)] px-2.5 py-1 text-xs font-semibold text-[var(--brand)]">
                  {t("unit")} {est.unit_number}
                </span>
                <div className="flex items-center gap-2">
                  {est.is_deleted ? (
                    <span className="rounded-md bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-500">{t("deleted")}</span>
                  ) : null}
                  <CategoryBadge label={t(est.category)} color={CategoryColors[est.category]} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-800">{est.engineer_name}</span>
                <span className="text-xs text-slate-400">{formatDate(est.created_at)}</span>
              </div>
              {est.estimation_number ? (
                <div className="mt-1 text-xs text-slate-500">
                  {t("estimation_num")}: {est.estimation_number} · {est.rows?.length || 0} {t("rows_label")}
                  {est.final_total ? ` · ${t("final_total")}: ${fmtMoney(est.final_total, est.currency)}` : ""}
                </div>
              ) : null}
              {est.is_deleted && admin ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(est.id);
                  }}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500 py-2 text-xs font-semibold text-white"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> {t("restore")}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {showPicker ? (
        <FacilityPicker
          categories={userCategories(user)}
          title={t("which_section")}
          onClose={() => setShowPicker(false)}
          onSelect={(cat) => navigate(`/estimation/${cat}`)}
        />
      ) : null}
    </div>
  );
}
